import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { cases } from "../data/cases";
import FadeIn from "../components/FadeIn";

const Cases = () => {
    const { t, lang } = useLang();
    const [filter, setFilter] = useState("all");

    const filters = [
        { key: "all", label: t.cases.filterAll },
        { key: "renovation", label: t.cases.filterRenovation },
        { key: "extension", label: t.cases.filterExtension },
        { key: "summerhouse", label: t.cases.filterSummerhouse },
    ];

    const filtered =
        filter === "all" ? cases : cases.filter((c) => c.type === filter);

    return (
        <main data-testid="page-cases" className="bg-white text-[#111111]">
            {/* Header */}
            <section className="pt-40 md:pt-56 pb-16 md:pb-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6">
                    <FadeIn className="col-span-12 md:col-span-4">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888]">
                            {t.cases.eyebrow}
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.1} className="col-span-12 md:col-span-8">
                        <h1 className="font-serif font-light text-6xl md:text-8xl lg:text-9xl leading-[0.92] tracking-tight">
                            {t.cases.title}
                            <span className="italic text-[#D9D4CD]">.</span>
                        </h1>
                        <p className="mt-8 md:mt-12 max-w-xl text-sm md:text-base text-[#555] leading-relaxed">
                            {t.cases.intro}
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Filter */}
            <section className="border-t border-[#EAE8E4] sticky top-16 md:top-20 z-30 bg-white/95 backdrop-blur-sm">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-5 flex items-center gap-6 md:gap-8 overflow-x-auto">
                    {filters.map((f) => (
                        <button
                            key={f.key}
                            data-testid={`filter-${f.key}`}
                            onClick={() => setFilter(f.key)}
                            className={`text-[11px] tracking-luxury uppercase whitespace-nowrap transition-opacity ${
                                filter === f.key
                                    ? "opacity-100"
                                    : "opacity-50 hover:opacity-100"
                            }`}
                        >
                            {f.label}
                            <span className="ml-2 editorial-number not-italic">
                                {f.key === "all"
                                    ? cases.length
                                    : cases.filter((c) => c.type === f.key).length}
                            </span>
                        </button>
                    ))}
                </div>
            </section>

            {/* Grid */}
            <section className="py-16 md:py-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-12 gap-x-6 gap-y-20 md:gap-y-32">
                        {filtered.map((c, i) => {
                            // asymmetric pattern
                            const patterns = [
                                "col-span-12 md:col-span-7",
                                "col-span-12 md:col-span-5 md:mt-32",
                                "col-span-12 md:col-span-5 md:col-start-2",
                                "col-span-12 md:col-span-6 md:col-start-7",
                                "col-span-12 md:col-span-8 md:mt-12",
                                "col-span-12 md:col-span-4 md:col-start-9",
                            ];
                            const pattern = patterns[i % patterns.length];
                            return (
                                <FadeIn
                                    key={c.slug}
                                    delay={(i % 3) * 0.1}
                                    className={pattern}
                                >
                                    <Link
                                        to={`/cases/${c.slug}`}
                                        data-testid={`case-card-${c.slug}`}
                                        className="block group"
                                    >
                                        <div className="case-card aspect-[4/5] bg-[#EBE8E3]">
                                            <img
                                                src={c.hero}
                                                alt={c.title[lang]}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="mt-6 flex items-baseline justify-between gap-4">
                                            <div>
                                                <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-1">
                                                    {c.location[lang]}  ·  {c.year}
                                                </div>
                                                <h2 className="font-serif text-2xl md:text-3xl leading-tight">
                                                    {c.title[lang]}
                                                </h2>
                                                <p className="mt-2 font-serif italic text-[#555] text-base md:text-lg max-w-md">
                                                    {c.subtitle[lang]}
                                                </p>
                                            </div>
                                            <div className="editorial-number text-2xl whitespace-nowrap">
                                                {String(i + 1).padStart(2, "0")}
                                            </div>
                                        </div>
                                    </Link>
                                </FadeIn>
                            );
                        })}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Cases;
